import { Slide } from "react-toastify";

export const APP_CONSTANTS = {
  sanitizeHtmlConfig: {
    allowedTags: ["img", "p", "span", "div", "ul", "li"],
    allowedAttributes: {
      img: ["alt", "src", "style"],
      a: ["class", "href", "target", "rel"],
      p: ["*"],
      span: ["*"],
      div: ["*"],
    },
  },
  toasterConfig: {
    position: "top-center",
    autoClose: 5000,
    icon: true,
    transition: Slide,
    closeOnClick: true,
    toastClassName: "rounded-5",
  },
};

export const APP_MESSAGES = Object.freeze({
  defaultErrorMessage: "Something went wrong, please try again!",
});

export const Environments = Object.freeze({
  Dev: "dev",
  QA: "qa",
  Staging: "staging",
  Production: "production",
});

export const ListeningStatus = Object.freeze({
  Preparing: "PREPARING",
  Idle: "IDLE",
  StartingToListen: "STARTING_TO_LISTEN",
  Listening: "LISTENING",
  Processing: "PROCESSING",
  DoneProcessing: "DONE_PROCESSING",
});

export const MAX_CONTINUOUS_LISTENING_MINUTES = 2;
export const MAX_CONTINUOUS_IDLE_MINUTES = 5;

export const toastrTypes = ["error", "info", "warning"];

export const SliceOfConversationToPassToAPI = -5;

export const supportedLangauges = [
  {
    uid: "ar",
    langCode: "ar",
    langText: "Arabic - العربية",
    transcriptionDir: "rtl",
    dialects: [
      { dialectCode: "AE", dialectName: "United Arab Emirates" },
      { dialectCode: "BH", dialectName: "Bahrain" },
      { dialectCode: "DZ", dialectName: "Algeria" },
      { dialectCode: "EG", dialectName: "Egypt" },
      // { dialectCode: "IL", dialectName: "Israel" },
      { dialectCode: "IQ", dialectName: "Iraq" },
      { dialectCode: "JO", dialectName: "Jordan" },
      { dialectCode: "KW", dialectName: "Kuwait" },
      { dialectCode: "LB", dialectName: "Lebanon" },
      { dialectCode: "LY", dialectName: "Libya" },
      { dialectCode: "MA", dialectName: "Morocco" },
      { dialectCode: "OM", dialectName: "Oman" },
      { dialectCode: "PS", dialectName: "Palestine" },
      { dialectCode: "QA", dialectName: "Qatar" },
      { dialectCode: "SA", dialectName: "Saudi Arabia" },
      { dialectCode: "SY", dialectName: "Syria" },
      { dialectCode: "TN", dialectName: "Tunisia" },
      { dialectCode: "YE", dialectName: "Yemen" },
    ],
  },
  {
    uid: "bn",
    langCode: "bn",
    langText: "Bangla - বাংলা",
    dialects: [
      { dialectCode: "IN", dialectName: "India", dialectVoice: "bn-IN-BashkarNeural" },
      // Azure STT lists bn-IN; bn-BD appears for TTS, but not for STT. [1](https://docs.azure.cn/en-us/ai-services/speech-service/language-support)
    ],
  },
  {
    uid: "c-zh",
    langCode: "zh",
    langText: "Cantonese - 廣東話",
    dialects: [
      { dialectCode: "HK", dialectName: "Hong Kong (Traditional)", dialectVoice: "zh-HK-WanLungNeural", translationCode: "yue-HK" },
      {
        dialectCode: "CN",
        dialectName: "China (Simplified)",
        localeCode: "yue-CN",
        dialectVoice: "yue-CN-YunSongNeural",
      },
    ],
  },
  {
    uid: "en",
    langCode: "en",
    langText: "English",
    hidden: true,
    default: true,
    dialects: [
      { dialectCode: "AU", dialectName: "Australia" },
      { dialectCode: "CA", dialectName: "Canada" },
      { dialectCode: "GB", dialectName: "United Kingdom" },
      // { dialectCode: "GH", dialectName: "Ghana" },
      // { dialectCode: "HK", dialectName: "Hong Kong SAR" },
      { dialectCode: "IE", dialectName: "Ireland" },
      { dialectCode: "IN", dialectName: "India" },
      // { dialectCode: "KE", dialectName: "Kenya" },
      // { dialectCode: "NG", dialectName: "Nigeria" },
      { dialectCode: "NZ", dialectName: "New Zealand" },
      // { dialectCode: "PH", dialectName: "Philippines" },
      // { dialectCode: "SG", dialectName: "Singapore" },
      // { dialectCode: "TZ", dialectName: "Tanzania" },
      { dialectCode: "US", dialectName: "United States", default: true },
      { dialectCode: "ZA", dialectName: "South Africa" },
    ],
  },
  {
    uid: "fr",
    langCode: "fr",
    langText: "French - Français",
    dialects: [
      { dialectCode: "FR", dialectName: "France" },
      // { dialectCode: "CA", dialectName: "Canada" },
      // { dialectCode: "CH", dialectName: "Switzerland" },
      // { dialectCode: "BE", dialectName: "Belgium" }
    ],
  },
  {
    uid: "ko",
    langCode: "ko",
    langText: "Korean - 한국어",
    dialects: [{ dialectCode: "KR", dialectName: "Korea" }],
  },
  {
    uid: "m-zh",
    langCode: "zh",
    langText: "Mandarin - 普通话",
    dialects: [
      { dialectCode: "CN", dialectName: "China (Mandarin, Simplified)" },
      // Optional: Azure lists zh-TW (Taiwanese Mandarin, Traditional) as a separate locale; add if you want a broader “Mandarin” bucket:
      { dialectCode: "TW", dialectName: "Taiwan (Mandarin, Traditional)" },
      { dialectCode: "HK", dialectName: "Hong Kong (Mandarin, Traditional)" },
      {
        dialectCode: "cn-sichuan",
        dialectName: "China (Southwestern Mandarin)",
      },
      // Azure also lists zh-cn-shandong and zh-cn-sichuan (regional Mandarin variants in CN). [1](https://docs.azure.cn/en-us/ai-services/speech-service/language-support)
    ],
  },
  {
    uid: "ru",
    langCode: "ru",
    langText: "Russian - Русский",
    dialects: [{ dialectCode: "RU", dialectName: "Russia" }],
  },
  {
    uid: "es",
    langCode: "es",
    langText: "Spanish - Español",
    dialects: [
      { dialectCode: "US", dialectName: "United States" },
      { dialectCode: "AR", dialectName: "Argentina" },
      { dialectCode: "BO", dialectName: "Bolivia" },
      { dialectCode: "CL", dialectName: "Chile" },
      { dialectCode: "CO", dialectName: "Colombia" },
      { dialectCode: "CR", dialectName: "Costa Rica" },
      { dialectCode: "CU", dialectName: "Cuba" },
      { dialectCode: "DO", dialectName: "Dominican Republic" },
      { dialectCode: "EC", dialectName: "Ecuador" },
      { dialectCode: "ES", dialectName: "Spain" },
      { dialectCode: "GQ", dialectName: "Equatorial Guinea" },
      { dialectCode: "GT", dialectName: "Guatemala" },
      { dialectCode: "HN", dialectName: "Honduras" },
      { dialectCode: "MX", dialectName: "Mexico" },
      { dialectCode: "NI", dialectName: "Nicaragua" },
      { dialectCode: "PA", dialectName: "Panama" },
      { dialectCode: "PE", dialectName: "Peru" },
      { dialectCode: "PR", dialectName: "Puerto Rico" },
      { dialectCode: "PY", dialectName: "Paraguay" },
      { dialectCode: "SV", dialectName: "El Salvador" },
      { dialectCode: "UY", dialectName: "Uruguay" },
      { dialectCode: "VE", dialectName: "Venezuela" },
    ],
  },
  {
    uid: "ur",
    langCode: "ur",
    langText: "Urdu - اردو",
    transcriptionDir: "rtl",
    dialects: [
      { dialectCode: "IN", dialectName: "India", dialectVoice: "ur-IN-SalmanNeural" },
      // Azure STT lists ur-IN; ur-PK isn't listed for STT in this table. [1](https://docs.azure.cn/en-us/ai-services/speech-service/language-support)
    ],
  },
  {
    uid: "sq",
    langCode: "sq",
    langText: "Albanian - Shqip",
    dialects: [{ dialectCode: "AL", dialectName: "Albania" }],
  },
  {
    uid: "uz",
    langCode: "uz",
    langText: "Uzbek - O'zbek",
    dialects: [{ dialectCode: "UZ", dialectName: "Uzbekistan" }],
  },
  {
    uid: "uk",
    langCode: "uk",
    langText: "Ukrainian - Українська",
    dialects: [{ dialectCode: "UA", dialectName: "Ukraine", dialectVoice: "uk-UA-OstapNeural" }],
  },
  ///// Do validate every field for Haitian Creole before enabling
  {
    uid: "ht",
    langCode: "ht",
    langText: "Haitian Creole -  Kreyòl ayisyen",
    dialects: [{ dialectCode: "HT", dialectName: "Haiti" }],
  },
];

export const Report_Types = [
  { value: "SESSIONS", name: "Sessions per User per Language" },
  { value: "CONVERSATION", name: "Conversation Duration per School and Language" },
  { value: "FEEDBACK", name: "User Feedback Report" }
];
